import React, { useState, useEffect } from 'react';
import { X, Package, MapPin, Hash, Weight, Calendar, User, Clock, ChevronDown, ChevronUp, Info, Truck } from 'lucide-react';
import { PListDetailsDto } from '../../../../types/picking';
import { OrdersDto } from '../../../../types/dataInspection';
import { pickingService } from '../../../../services/pickingService';
import { dataInspectionService } from '../../../../services/dataInspectionService';

interface PreviewPickingModalProps {
  isOpen: boolean;
  pListId: number | null;
  onClose: () => void;
}

export const PreviewPickingModal: React.FC<PreviewPickingModalProps> = ({
  isOpen,
  pListId,
  onClose
}) => {
  const [pickingDetails, setPickingDetails] = useState<PListDetailsDto | null>(null);
  const [orderDetails, setOrderDetails] = useState<OrdersDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);

  useEffect(() => {
    if (isOpen && pListId) {
      loadPickingDetails();
    }
  }, [isOpen, pListId]);

  const loadPickingDetails = async () => {
    if (!pListId) return;

    try {
      setLoading(true);
      setError(null);
      const details = await pickingService.getPickingDetails(pListId);
      setPickingDetails(details);
      
      // Hent ordredetaljer fra første linje
      if (details.lines.length > 0) {
        const firstOrderNo = details.lines[0].orderNo;
        try {
          const orderData = await dataInspectionService.getOrderWithLines(firstOrderNo);
          setOrderDetails(orderData.header);
        } catch (orderErr) {
          console.error('Error loading order details:', orderErr);
          // Fortsett selv om ordre-henting feiler
        }
      }
    } catch (err) {
      console.error('Error loading picking details:', err);
      setError('Kunne ikke laste plukk-detaljer. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusText = (status: number) => {
    switch (status) {
      case 0: return 'Ubehandlet';
      case 10: return 'Godkjent';
      case 20: return 'Under arbeid';
      case 30: return 'Behandlet, ikke komplett';
      case 40: return 'Behandlet, komplett';
      case 45: return 'Til pakking';
      case 50: return 'Klar for eksport';
      case 60: return 'Eksportert';
      default: return `Status ${status}`;
    }
  };

  const getPriorityColor = (priority: number) => {
    if (priority >= 900) return 'bg-red-500';
    if (priority >= 500) return 'bg-orange-500';
    return 'bg-blue-500';
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('no-NO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white sm:rounded-lg shadow-xl w-full h-full sm:h-auto sm:max-w-7xl sm:max-h-[90vh] flex flex-col overflow-hidden">
        {/* Modal Header - Sticky on mobile */}
        <div className="bg-gray-50 px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Package className="text-blue-600 flex-shrink-0" size={20} />
            <h2 className="text-base sm:text-xl font-semibold text-gray-900 truncate">
              {pickingDetails ? `Plukkliste #${pickingDetails.header.pListID}` : 'Forhåndsvisning'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <X size={24} />
          </button>
        </div>

        {/* Modal Content */}
        <div className="flex-1 p-3 sm:p-6 overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 animate-spin border-2 border-blue-500 border-t-transparent rounded-full" />
              <span className="ml-3 text-gray-600">Laster plukk-detaljer...</span>
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <p className="text-red-600 mb-4">{error}</p>
              <button 
                onClick={loadPickingDetails}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                Prøv igjen
              </button>
            </div>
          )}

          {pickingDetails && (
            <div className="flex-1 flex flex-col space-y-6">
              {/* Collapsible Header Information */}
              <div className="bg-gray-50 rounded-lg overflow-hidden">
                {/* Header Toggle Button */}
                <button
                  onClick={() => setIsHeaderExpanded(!isHeaderExpanded)}
                  className="w-full px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between text-left hover:bg-gray-100 transition-colors duration-200 min-h-[44px]"
                >
                  <div className="flex items-center gap-2">
                    <Info className="text-blue-600" size={20} />
                    <h3 className="text-lg font-semibold text-gray-900">
                      Hodeinformasjon
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">
                      {isHeaderExpanded ? 'Skjul detaljer' : 'Vis detaljer'}
                    </span>
                    {isHeaderExpanded ? (
                      <ChevronUp className="text-gray-400" size={20} />
                    ) : (
                      <ChevronDown className="text-gray-400" size={20} />
                    )}
                  </div>
                </button>

                {/* Expandable Content with Scrollbar */}
                <div className={`transition-all duration-300 ease-in-out ${
                  isHeaderExpanded ? 'max-h-[60vh] sm:max-h-96' : 'max-h-0'
                } overflow-hidden`}>
                  <div className="overflow-y-auto max-h-[60vh] sm:max-h-96 px-3 sm:px-6 pb-4 sm:pb-6 border-t border-gray-200">
                    
                    {/* 1. Beskrivelse 1 og 2 - RED BOX */}
                    {(pickingDetails.header.description1 || pickingDetails.header.description2) && (
                      <div className="mt-4">
                        {pickingDetails.header.description1 && (
                          <div className="mb-2">
                            <span className="text-sm text-gray-500">Beskrivelse 1:</span>
                            <p className="font-medium">{pickingDetails.header.description1}</p>
                          </div>
                        )}
                        {pickingDetails.header.description2 && (
                          <div>
                            <span className="text-sm text-gray-500">Beskrivelse 2:</span>
                            <p className="font-medium">{pickingDetails.header.description2}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* 2. Leveringsinformasjon fra Order - BLUE BOX */}
                    {orderDetails && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <Truck size={16} className="text-blue-600" />
                          Leveringsinformasjon
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {orderDetails.carrierName && (
                            <div className="flex items-start gap-3">
                              <Truck className="text-gray-400 mt-0.5" size={16} />
                              <div>
                                <span className="text-sm text-gray-500">Transportør:</span>
                                <p className="font-medium">{orderDetails.carrierName}</p>
                              </div>
                            </div>
                          )}
                          
                          {orderDetails.deliveryMethod && (
                            <div className="flex items-start gap-3">
                              <Package className="text-gray-400 mt-0.5" size={16} />
                              <div>
                                <span className="text-sm text-gray-500">Leveringsmåte:</span>
                                <p className="font-medium">{orderDetails.deliveryMethod}</p>
                              </div>
                            </div>
                          )}
                          
                          {(orderDetails.delAddress1 || orderDetails.delAddress2 || orderDetails.delZipCode || orderDetails.delCity) && (
                            <div className="flex items-start gap-3 md:col-span-2 lg:col-span-1">
                              <MapPin className="text-gray-400 mt-0.5" size={16} />
                              <div>
                                <span className="text-sm text-gray-500">Leveringsadresse:</span>
                                <div className="font-medium">
                                  {orderDetails.delAddress1 && <p>{orderDetails.delAddress1}</p>}
                                  {orderDetails.delAddress2 && <p>{orderDetails.delAddress2}</p>}
                                  {(orderDetails.delZipCode || orderDetails.delCity) && (
                                    <p>{orderDetails.delZipCode} {orderDetails.delCity}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* 3. Prioritet, Status, Operatør etc. - GREEN BOX */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${getPriorityColor(pickingDetails.header.priority)}`} />
                          <div>
                            <span className="text-sm text-gray-500">Prioritet:</span>
                            <p className="font-medium">{pickingDetails.header.priority}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <Hash className="text-gray-400" size={16} />
                          <div>
                            <span className="text-sm text-gray-500">Status:</span>
                            <p className="font-medium">{getStatusText(pickingDetails.header.pListStatus)}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <User className="text-gray-400" size={16} />
                          <div>
                            <span className="text-sm text-gray-500">Operatør:</span>
                            <p className="font-medium">{pickingDetails.header.operatorID || 'Ikke tildelt'}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <Package className="text-gray-400" size={16} />
                          <div>
                            <span className="text-sm text-gray-500">Antall linjer:</span>
                            <p className="font-medium">{pickingDetails.header.numOfOrderLines}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <Weight className="text-gray-400" size={16} />
                          <div>
                            <span className="text-sm text-gray-500">Vekt:</span>
                            <p className="font-medium">{pickingDetails.header.weight ? `${pickingDetails.header.weight} kg` : '-'}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <Calendar className="text-gray-400" size={16} />
                          <div>
                            <span className="text-sm text-gray-500">Leveringsdato:</span>
                            <p className="font-medium">{formatDate(pickingDetails.header.deliveryDate)}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {pickingDetails.header.startWorkDateTime && (
                      <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center gap-3">
                          <Clock className="text-gray-400" size={16} />
                          <div>
                            <span className="text-sm text-gray-500">Startet:</span>
                            <p className="font-medium">{formatDate(pickingDetails.header.startWorkDateTime)}</p>
                          </div>
                        </div>
                        {pickingDetails.header.stopWorkDateTime && (
                          <div className="flex items-center gap-3">
                            <Clock className="text-gray-400" size={16} />
                            <div>
                              <span className="text-sm text-gray-500">Avsluttet:</span>
                              <p className="font-medium">{formatDate(pickingDetails.header.stopWorkDateTime)}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Lines Information */}
              <div className="flex-1 flex flex-col">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                  <Package size={20} />
                  Plukk-linjer ({pickingDetails.lines.length})
                </h3>

                {/* Mobile: Card layout */}
                <div className="sm:hidden space-y-2">
                  {pickingDetails.lines.map((line, index) => {
                    const isCompleted = line.qtyPicked > 0;
                    const isFullyPicked = line.qtyPicked >= line.qty;

                    return (
                      <div
                        key={`${line.pListLineNo}-${index}`}
                        className={`rounded-lg border p-3 ${
                          isCompleted
                            ? isFullyPicked
                              ? 'bg-green-50 border-green-200'
                              : 'bg-yellow-50 border-yellow-200'
                            : 'bg-white border-gray-200'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="font-medium text-gray-900 text-sm">
                            #{line.pListLineNo} - {line.itemNo}
                          </div>
                          {isCompleted ? (
                            isFullyPicked ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Komplett
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                Delvis
                              </span>
                            )
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              Venter
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                          <div className="flex items-center gap-1">
                            <MapPin size={12} className="text-gray-400" />
                            {line.binCode || '-'}
                          </div>
                          <div className="text-right">
                            Ordre: {line.orderNo}
                          </div>
                          <div>
                            Bestilt: <span className="font-medium">{line.qty}</span>
                          </div>
                          <div className="text-right">
                            Plukket: <span className={`font-medium ${
                              isCompleted
                                ? isFullyPicked ? 'text-green-700' : 'text-yellow-700'
                                : ''
                            }`}>{line.qtyPicked}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Desktop: Table layout */}
                <div className="hidden sm:block bg-white border border-gray-200 rounded-lg overflow-hidden flex-1 flex flex-col">
                  <div className="overflow-x-auto flex-1 overflow-y-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Linje
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Varenr.
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Ordrenr.
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Lokasjon
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Bestilt
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Reservert
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Plukket
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {pickingDetails.lines.map((line, index) => {
                          const isCompleted = line.qtyPicked > 0;
                          const isFullyPicked = line.qtyPicked >= line.qty;
                          const isPartiallyPicked = line.qtyPicked > 0 && line.qtyPicked < line.qty;

                          return (
                            <tr
                              key={`${line.pListLineNo}-${index}`}
                              className={
                                isCompleted
                                  ? isFullyPicked
                                    ? 'bg-green-50'
                                    : 'bg-yellow-50'
                                  : 'bg-white hover:bg-gray-50'
                              }
                            >
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                {line.pListLineNo}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {line.itemNo}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {line.orderNo}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                <div className="flex items-center gap-1">
                                  <MapPin size={14} className="text-gray-400" />
                                  {line.binCode || '-'}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-gray-700">
                                {line.qty}
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-gray-700">
                                {line.qtyReserved}
                              </td>
                              <td className="px-4 py-3 text-sm text-right font-medium">
                                <span className={
                                  isCompleted
                                    ? isFullyPicked
                                      ? 'text-green-700'
                                      : 'text-yellow-700'
                                    : 'text-gray-700'
                                }>
                                  {line.qtyPicked}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                {isCompleted ? (
                                  isFullyPicked ? (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      Komplett
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                      Delvis
                                    </span>
                                  )
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    Ikke startet
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Summary Statistics */}
                <div className="mt-3 sm:mt-4 grid grid-cols-3 gap-2 sm:gap-4">
                  <div className="bg-green-50 p-2 sm:p-3 rounded-lg">
                    <div className="text-xs sm:text-sm text-green-600 font-medium">Komplette</div>
                    <div className="text-base sm:text-lg font-bold text-green-900">
                      {pickingDetails.lines.filter(l => l.qtyPicked >= l.qty).length}
                    </div>
                  </div>
                  <div className="bg-yellow-50 p-2 sm:p-3 rounded-lg">
                    <div className="text-xs sm:text-sm text-yellow-600 font-medium">Delvis</div>
                    <div className="text-base sm:text-lg font-bold text-yellow-900">
                      {pickingDetails.lines.filter(l => l.qtyPicked > 0 && l.qtyPicked < l.qty).length}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-2 sm:p-3 rounded-lg">
                    <div className="text-xs sm:text-sm text-gray-600 font-medium">Venter</div>
                    <div className="text-base sm:text-lg font-bold text-gray-900">
                      {pickingDetails.lines.filter(l => l.qtyPicked === 0).length}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>


      </div>
    </div>
  );
};
